import React, { useState, useEffect, useRef } from 'react';
import { UserCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiUtils';

/**
 * Autocomplete dropdown for @mentions
 * Shows when user types "@" in a textarea
 */
const MentionAutocomplete = ({
  query,
  onSelect,
  onClose,
  position = { top: 0, left: 0 }
}) => {
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch results when query changes
  useEffect(() => {
    if (query && query.length >= 1) {
      fetchResults(query);
    } else {
      setResults([]);
    }
  }, [query]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % results.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [results, selectedIndex, onSelect, onClose]);

  const fetchResults = async (searchQuery) => {
    try {
      setLoading(true);
      const response = await apiRequest(`/mentions/search?q=${encodeURIComponent(searchQuery)}&limit=10`);

      if (response.success) {
        setResults(response.data || []);
        setSelectedIndex(0); // Reset selection when results change
      }
    } catch (error) {
      console.error('Error fetching mention suggestions:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  if (!query && !loading && results.length === 0) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 w-64 bg-white rounded-lg shadow-lg border border-secondary-200 max-h-60 overflow-y-auto"
      style={{ top: position.top, left: position.left }}
    >
      {loading ? (
        <div className="px-4 py-3 text-sm text-secondary-500">
          Loading...
        </div>
      ) : results.length === 0 ? (
        <div className="px-4 py-3 text-sm text-secondary-500">
          No results found
        </div>
      ) : (
        <ul className="py-1">
          {results.map((result, index) => (
            <li
              key={result.id}
              className={`px-4 py-2 cursor-pointer flex items-center space-x-3 ${
                index === selectedIndex
                  ? 'bg-primary-50 text-primary-700'
                  : 'hover:bg-secondary-50'
              }`}
              onClick={() => onSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {/* Avatar/Icon */}
              <div className="flex-shrink-0">
                {result.type === 'user' ? (
                  result.avatar_url ? (
                    <img
                      src={result.avatar_url}
                      alt={result.display}
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <UserCircleIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  )
                ) : (
                  <div
                    className="h-8 w-8 rounded flex items-center justify-center"
                    style={{ backgroundColor: result.color || '#3b82f6' }}
                  >
                    <UserGroupIcon className="h-5 w-5 text-white" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-secondary-900 truncate">
                  {result.display}
                </div>
                <div className="text-xs text-secondary-500 truncate">
                  {result.type === 'user' ? result.email : result.description}
                </div>
              </div>

              {/* Mention preview */}
              <div className="flex-shrink-0 text-xs text-secondary-400">
                @{result.mention}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MentionAutocomplete;
